import React, { createContext, useContext, useState, ReactNode, useEffect, useCallback } from 'react';

interface MetricDelta {
  delta: number;
  ts: number;
}

interface MetricDeltaContextType {
  adjustMetric: (entityId: string, entityType: string, metricName: string, delta: number) => void;
  resetMetric: (entityId: string, entityType: string, metricName: string) => void;
  getOptimisticCount: (baseCount: number, entityId: string, entityType: string, metricName: string) => number;
}

const MetricDeltaContext = createContext<MetricDeltaContextType | undefined>(undefined);

/**
 * 🌍 MetricDeltaProvider
 * A lightweight, root-level provider that tracks session-based numeric adjustments.
 * This ensures "instant feedback" for all user contributions (likes, friends, slaps, etc).
 */
export function MetricDeltaProvider({ children }: { children: ReactNode }) {
  // Key format: `${entityType}:${entityId}:${metricName}`
  const [localDeltas, setLocalDeltas] = useState<Record<string, MetricDelta>>({});

  // Cleanup stale adjustments every 10 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      const STALE_TIMEOUT = 60000; // 60 seconds — enough for Firestore to propagate and visitorUser to refresh
      
      setLocalDeltas(prev => {
        const next = { ...prev };
        let changed = false;
        for (const key in next) {
          if (now - next[key].ts > STALE_TIMEOUT) {
            delete next[key];
            changed = true;
          }
        }
        return changed ? next : prev;
      });
    }, 5000);
    
    return () => clearInterval(interval);
  }, []);

  const adjustMetric = useCallback((entityId: string, entityType: string, metricName: string, delta: number) => {
    const key = `${entityType}:${entityId}:${metricName}`;
    setLocalDeltas(prev => ({
      ...prev,
      [key]: {
        delta: (prev[key]?.delta || 0) + delta,
        ts: Date.now()
      }
    }));
  }, []);

  const resetMetric = useCallback((entityId: string, entityType: string, metricName: string) => {
    const key = `${entityType}:${entityId}:${metricName}`;
    setLocalDeltas(prev => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const getOptimisticCount = useCallback((baseCount: number, entityId: string, entityType: string, metricName: string): number => {
    const key = `${entityType}:${entityId}:${metricName}`;
    const adj = localDeltas[key]?.delta || 0;
    return Math.max(0, (Number(baseCount) || 0) + adj);
  }, [localDeltas]);

  return (
    <MetricDeltaContext.Provider value={{ adjustMetric, resetMetric, getOptimisticCount }}>
      {children}
    </MetricDeltaContext.Provider>
  );
}

export function useMetricDelta() {
  const context = useContext(MetricDeltaContext);
  if (context === undefined) {
    throw new Error('useMetricDelta must be used within a MetricDeltaProvider');
  }
  return context;
}
